import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CAvatar,
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CRow,
  CSpinner,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilPencil, cilTrash, cilX, cilZoom } from "@coreui/icons";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

import { apiClient } from "services/Api";
import DataTableComponent from "components/DataTableComponent";
import ToastNotification from "components/globals/ToastNotification";
import userAvatar from "assets/images/avatars/user.png";

const qualityLabels = {
  0: "Má",
  1: "Fraca",
  2: "Insuficiente",
  3: "Boa",
  4: "Muito Boa",
  5: "Excelente",
};

const recommendationLabels = {
  0: "Não se Aplica",
  1: "Definitivamente Não",
  2: "Muito Provavelmente Não",
  3: "Provavelmente Não",
  4: "Provavelmente Sim",
  5: "Muito Provavelmente Sim",
  6: "Definitivamente Sim",
};

const getLabel = (value, map) => {
  if (value === null || value === undefined) return "-";
  return Object.prototype.hasOwnProperty.call(map, value) ? map[value] : value;
};

const getIssueLabel = (value) => {
  if (value === true) return "Sim";
  if (value === false) return "Não";
  return "-";
};

const PresentialSatisfactionSurveyList = () => {
  const navigate = useNavigate();
  const [toast, setToast] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isLoading, setIsLoading] = useState(true);
  const [previewSurvey, setPreviewSurvey] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [distributions, setDistributions] = useState(null);

  const handleNavigate = (path) => {
    setPreviewSurvey(null);
    setTimeout(() => navigate(path), 0);
  };

  useEffect(() => {
    const fetchSurveys = async () => {
      setIsLoading(true);
      try {
        const response = await apiClient.get(
          `/presential-satisfaction-surveys/?year=${selectedYear}`,
        );
        setSurveys(response.data.surveys || []);
        setAvailableYears(response.data.available_years || []);
        setKpis(response.data.kpis || null);
        setDistributions(response.data.distributions || null);
        if (
          response.data.available_years &&
          response.data.available_years.length > 0 &&
          !response.data.available_years.includes(Number(selectedYear))
        ) {
          setSelectedYear(response.data.available_years[0]);
        }
      } catch (error) {
        setToast({
          title: "Questionário Presencial",
          message: "Erro a carregar os questionários presenciais",
          type: "error",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchSurveys();
  }, [selectedYear]);

  const handleDelete = async (surveyId) => {
    if (!window.confirm("Tem a certeza que quer eliminar este registo?")) {
      return;
    }

    try {
      await apiClient.delete(`/presential-satisfaction-surveys/${surveyId}/`);
      setSurveys(surveys.filter((survey) => survey.id !== surveyId));
      setPreviewSurvey(null);
    } catch (error) {
      setToast({
        title: "Questionário Presencial",
        message: "Erro ao eliminar o questionário presencial",
        type: "error",
      });
    }
  };

  const columns = [
    {
      name: "Data",
      selector: (row) => row.created_at || "-",
      sortable: true,
      className: "text-center",
    },
    {
      name: "Cliente",
      selector: (row) => row.client_name || "-",
      sortable: true,
    },
    {
      name: "Qualidade",
      selector: (row) => getLabel(row.service_quality, qualityLabels),
      sortable: true,
    },
    {
      name: "Preenchido por",
      selector: (row) => row.created_by_name || "-",
      sortable: true,
    },
  ];

  const getDistributionTotal = (items) =>
    (items || []).reduce((sum, item) => sum + (item.count || 0), 0);

  const buildPieOptions = (title, items, labels) => ({
    chart: {
      type: "pie",
      height: 180,
      spacing: [4, 4, 4, 4],
      backgroundColor: "transparent",
    },
    title: { text: null },
    credits: { enabled: false },
    exporting: { enabled: false },
    tooltip: {
      pointFormat: "<b>{point.y}</b> ({point.percentage:.0f}%)",
    },
    plotOptions: {
      pie: {
        innerSize: "62%",
        borderWidth: 0,
        showInLegend: true,
        dataLabels: { enabled: false },
      },
    },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      symbolRadius: 4,
      symbolHeight: 10,
      symbolWidth: 10,
      itemStyle: { fontSize: "11px" },
    },
    series: [
      {
        name: title,
        colorByPoint: true,
        data: (items || []).map((item) => ({
          name:
            labels[item.value] !== undefined ? labels[item.value] : item.value,
          y: item.count,
        })),
      },
    ],
  });

  const renderDistribution = (title, avgValue, items, labels) => {
    const total = getDistributionTotal(items);
    return (
      <CCard className=" h-100 shadow-sm">
        <CCardBody className="py-3">
          <div className="text-uppercase text-medium-emphasis small mb-1">
            {title}
          </div>
          <div className="d-flex align-items-baseline gap-2 mb-3">
            <div className="fs-5 fw-semibold">
              {avgValue === null || avgValue === undefined
                ? "-"
                : avgValue.toFixed(2)}
            </div>
            <div className="text-medium-emphasis small">média</div>
          </div>
          {total === 0 ? (
            <div className="text-medium-emphasis small">
              Sem respostas no período.
            </div>
          ) : (
            <HighchartsReact
              highcharts={Highcharts}
              options={buildPieOptions(title, items, labels)}
            />
          )}
        </CCardBody>
      </CCard>
    );
  };

  return (
    <>
      <ToastNotification toast={toast} setToast={setToast} />

      <CRow className="g-3 mb-4">
        <CCol sm={6} lg={4}>
          <CCard className="surveillance-kpi kpi-total h-100 shadow-sm">
            <CCardBody className="py-3">
              <div className="text-uppercase text-medium-emphasis small">
                Ano
              </div>
              <div className="fs-4 fw-semibold">{selectedYear}</div>
              <div className="text-medium-emphasis small">
                Período selecionado
              </div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6} lg={4}>
          <CCard className="surveillance-kpi kpi-completion h-100 shadow-sm">
            <CCardBody className="py-3">
              <div className="text-uppercase text-medium-emphasis small">
                Nº de inquéritos
              </div>
              <div className="fs-4 fw-semibold">{kpis?.total ?? 0}</div>
              <div className="text-medium-emphasis small">Total registado</div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6} lg={4}>
          <CCard className="surveillance-kpi kpi-pending h-100 shadow-sm">
            <CCardBody className="py-3">
              <div className="text-uppercase text-medium-emphasis small">
                Média global
              </div>
              <div className="fs-4 fw-semibold">
                {kpis?.global_average !== null &&
                kpis?.global_average !== undefined
                  ? kpis.global_average.toFixed(2)
                  : "-"}
              </div>
              <div className="text-medium-emphasis small">
                Escala consolidada
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CRow className="g-3 mb-4">
        <CCol lg={4}>
          {renderDistribution(
            "Qualidade do serviço",
            kpis?.avg_by_question?.service_quality,
            distributions?.service_quality,
            qualityLabels,
          )}
        </CCol>
        <CCol lg={4}>
          {renderDistribution(
            "Próximo projeto",
            kpis?.avg_by_question?.future_project,
            distributions?.future_project,
            recommendationLabels,
          )}
        </CCol>
        <CCol lg={4}>
          {renderDistribution(
            "Recomendação",
            kpis?.avg_by_question?.recommendation,
            distributions?.recommendation,
            recommendationLabels,
          )}
        </CCol>
      </CRow>

      <CCard>
        <CCardHeader className="d-flex justify-content-between align-items-center">
          <h3 className="mb-0">Questionário de Satisfação Presencial</h3>
          <Link to="/questionarios-satisfacao-presencial/new">
            <CButton color="info" className="text-white">
              Novo Questionário
            </CButton>
          </Link>
        </CCardHeader>
        <CCardBody>
          {isLoading ? (
            <div className="text-center">
              <CSpinner color="primary" />
            </div>
          ) : (
            <DataTableComponent
              columns={columns}
              data={surveys}
              years={availableYears.length > 0 ? availableYears : null}
              selectedYear={selectedYear}
              onYearChange={(newYear) => setSelectedYear(newYear)}
              onRowClicked={(row) => setPreviewSurvey(row)}
            />
          )}
        </CCardBody>
      </CCard>

      {previewSurvey && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "transparent",
            zIndex: 1040,
          }}
          onClick={() => setPreviewSurvey(null)}
          aria-hidden="true"
        />
      )}

      <COffcanvas
        placement="end"
        visible={Boolean(previewSurvey)}
        onHide={() => setPreviewSurvey(null)}
        backdrop={false}
        scrollable
        scroll
        style={{ width: "560px", maxWidth: "95vw" }}
      >
        <COffcanvasHeader className="border-bottom">
          <COffcanvasTitle className="d-flex align-items-center me-auto">
            <div>
              <div className="fw-semibold">Questionário Presencial</div>
              <div className="text-medium-emphasis small">
                #{previewSurvey?.id || "-"}
              </div>
            </div>
          </COffcanvasTitle>
          <div className="d-flex align-items-center gap-2">
            {previewSurvey && (
              <>
                <CButton
                  color="info"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handleNavigate(
                      `/questionarios-satisfacao-presencial/${previewSurvey.id}?view=1`,
                    )
                  }
                >
                  <CIcon icon={cilZoom} className="me-1" />
                  Ver
                </CButton>
                <CButton
                  color="info"
                  size="sm"
                  className="text-white"
                  onClick={() =>
                    handleNavigate(
                      `/questionarios-satisfacao-presencial/${previewSurvey.id}`,
                    )
                  }
                >
                  <CIcon icon={cilPencil} className="me-1" />
                  Editar
                </CButton>
                <CButton
                  color="danger"
                  size="sm"
                  className="text-white"
                  onClick={() => handleDelete(previewSurvey.id)}
                >
                  <CIcon icon={cilTrash} className="me-1" />
                  Eliminar
                </CButton>
              </>
            )}
            <CButton
              color="link"
              className="text-body p-0"
              onClick={() => setPreviewSurvey(null)}
              title="Fechar"
            >
              <CIcon icon={cilX} size="lg" />
            </CButton>
          </div>
        </COffcanvasHeader>
        <COffcanvasBody>
          {previewSurvey ? (
            <div className="d-flex flex-column gap-3">
              <div className="d-flex align-items-center gap-3">
                <CAvatar
                  src={previewSurvey.created_by_avatar_url || userAvatar}
                  size="lg"
                />
                <div className="flex-grow-1">
                  <div className="fw-semibold">
                    {previewSurvey.created_by_name || "-"}
                  </div>
                  <div className="text-medium-emphasis small">
                    {previewSurvey.created_at || "-"}
                  </div>
                </div>
                <CBadge color="info">
                  {getLabel(previewSurvey.service_quality, qualityLabels)}
                </CBadge>
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Cliente
                </div>
                <div className="fw-semibold">
                  {previewSurvey.client_name || "-"}
                </div>
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  O que faria de diferente?
                </div>
                <div className="fw-semibold">
                  {previewSurvey.project_change || "-"}
                </div>
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Comentários sobre a qualidade do serviço
                </div>
                <div className="fw-semibold">
                  {previewSurvey.service_quality_comment || "-"}
                </div>
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Houve algum aspeto que correu mal?
                </div>
                <div className="fw-semibold">
                  {getIssueLabel(previewSurvey.issue_occurred)}
                </div>
              </div>

              {previewSurvey.issue_occurred ? (
                <>
                  <div className="border rounded-3 p-3 bg-body-tertiary">
                    <div className="text-uppercase text-medium-emphasis small">
                      Qual
                    </div>
                    <div className="fw-semibold">
                      {previewSurvey.issue_detail || "-"}
                    </div>
                  </div>
                  <div className="border rounded-3 p-3 bg-body-tertiary">
                    <div className="text-uppercase text-medium-emphasis small">
                      Gestão do problema
                    </div>
                    <div className="fw-semibold">
                      {previewSurvey.issue_management || "-"}
                    </div>
                  </div>
                </>
              ) : null}

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Próximo projeto
                </div>
                <div className="fw-semibold">
                  {getLabel(previewSurvey.future_project, recommendationLabels)}
                </div>
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Recomendação
                </div>
                <div className="fw-semibold">
                  {getLabel(previewSurvey.recommendation, recommendationLabels)}
                </div>
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Sugestões
                </div>
                <div className="fw-semibold">
                  {previewSurvey.suggestions || "-"}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-muted">
              Clique num questionário para ver o detalhe.
            </div>
          )}
        </COffcanvasBody>
      </COffcanvas>
    </>
  );
};

export default PresentialSatisfactionSurveyList;
